import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

interface TenantSettings {
  default_duration?: number;
  questions_per_interview?: number;
  auto_invite_enabled?: boolean;
  [key: string]: unknown;
}

interface CompanyProfile {
  company_name?: string;
  website?: string;
  contact_email?: string;
  contact_phone?: string;
  mission_statement?: string;
  vision_statement?: string;
  values_statement?: string;
  founded_year?: number;
  headquarters_location?: string;
  culture_note?: string;
  perks_summary?: string;
}

interface Tenant {
  id: number;
  name: string;
  slug: string;
  plan?: string;
  is_demo?: boolean;
  subscription_start?: string | null;
  subscription_end?: string | null;
  company_name: string;
  description?: string;
  logo_url?: string;
  brand_colors?: any;
  website?: string;
  contact_email?: string;
  contact_phone?: string;
  thresholds?: any;
  benefits?: string[];
  ai_intro_text?: string;
  ai_language?: string;
  settings?: TenantSettings;
  smtp_server?: string | null;
  smtp_port?: number | null;
  smtp_username?: string | null;
  smtp_password?: string | null;
  from_email?: string | null;
  from_name?: string | null;
  company_profile?: CompanyProfile;
  smtp_invalid_since?: string | null;
}

interface TenantContextType {
  tenant: Tenant | null;
  loading: boolean;
  refreshTenant: () => Promise<void>;
  hasSmtpError: boolean;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const TenantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, user } = useAuth();

  const fetchTenant = useCallback(async () => {
    if (!isAuthenticated || !user?.tenant_id) {
      setTenant(null);
      return;
    }

    setLoading(true);
    try {
      const response = await api.get('/api/tenants/me');
      setTenant(response.data);
    } catch (error) {
      console.error('Failed to fetch tenant:', error);
      setTenant(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?.tenant_id]);

  useEffect(() => {
    fetchTenant();
  }, [fetchTenant]);

  useEffect(() => {
    if (!isAuthenticated || !user?.tenant_id) return;

    const handleFocus = () => {
      fetchTenant();
    };

    const interval = window.setInterval(() => {
      fetchTenant();
    }, 60000);

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.clearInterval(interval);
    };
  }, [fetchTenant, isAuthenticated, user?.tenant_id]);

  return (
    <TenantContext.Provider
      value={{
        tenant,
        loading,
        refreshTenant: fetchTenant,
        hasSmtpError: Boolean(tenant?.smtp_invalid_since),
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};
